<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2 pb-2">
            <div
              class="row bg-gradient-info shadow-black border-radius-lg pt-4 pb-3 mb-3"
            >
              <h6 class="col text-white text-capitalize ps-3">
                Participantes por árvore de conteúdo
              </h6>
            </div>

            <!-- Project -> course selection (course_id source for the report) -->
            <div class="row p-2 gy-3 gx-3 mb-3 col-lg-8 col-md-10 col-sm-12">
              <div class="col-12 col-md">
                <label class="mt-3">Projeto</label>
                <select
                  class="form-select form-select-lg content-tree-select"
                  v-model="selectedProjectId"
                  @change="handleProjectChange"
                >
                  <option value="">Selecione um projeto</option>
                  <option
                    v-for="projectItem in projects"
                    :key="projectItem.id"
                    :value="projectItem.id"
                  >
                    {{ projectItem.name }}
                  </option>
                </select>
              </div>

              <div class="col-12 col-md">
                <label class="mt-3">Curso</label>
                <select
                  class="form-select form-select-lg content-tree-select"
                  v-model="selectedCourseId"
                  :disabled="!selectedProjectId || coursesLoading"
                  @change="handleCourseChange"
                >
                  <option value="">Selecione um curso</option>
                  <option v-if="coursesLoading" value="" disabled>
                    Carregando cursos...
                  </option>
                  <option
                    v-for="course in courses"
                    :key="course.id"
                    :value="course.id"
                  >
                    {{ course.name }}
                  </option>
                </select>
                <p
                  v-if="selectedProjectId && !coursesLoading && courses.length === 0"
                  class="text-muted small mt-2 mb-0"
                >
                  Este projeto não possui cursos.
                </p>
              </div>
            </div>

            <!-- Initial state: nothing to load until a course is chosen -->
            <div
              v-if="!selectedCourseId"
              class="col-12 d-flex justify-content-center my-4"
            >
              <div class="col-md-6 text-center fw-bold mb-0 hint-box">
                Selecione um projeto e um curso para ver os participantes.
              </div>
            </div>

            <!-- Report area (only after a course is selected) -->
            <template v-else>
              <!-- Filters: kept mounted across requests so selection/chips persist -->
              <ParticipantsContentTreeFilters
                v-if="!error && filtersAvailable.length > 0"
                :filters-available="filtersAvailable"
                class="mb-2"
                @apply="onApplyFilters"
              />

              <Loading :message="loading_message" :loading="loading" />

              <!-- Error (400 / 404 / network) -->
              <div
                v-if="loading == false && error"
                class="col-12 d-flex justify-content-center"
              >
                <div class="col-md-6 ps-3 alert alert-danger fw-bold text-center">
                  {{ errorMessage }}
                </div>
              </div>

              <!-- Table + pagination -->
              <div v-else-if="loading == false">
                <!-- Empty: no participants for the current scope/filters -->
                <div
                  v-if="pagination.total === 0"
                  class="col-12 d-flex justify-content-center my-3"
                >
                  <div class="col-md-6 ps-3 alert alert-warning fw-bold text-center">
                    {{
                      hasActiveFilters
                        ? "Nenhum participante para os filtros selecionados."
                        : "Nenhum participante encontrado para este curso."
                    }}
                  </div>
                </div>

                <template v-else>
                  <ParticipantsContentTreeTable
                    v-if="rows.length > 0"
                    :columns="columns"
                    :rows="rows"
                  />

                  <!-- Out-of-range page: keep columns/pagination, allow going back -->
                  <div v-else class="col-12 d-flex justify-content-center my-3">
                    <div class="col-md-6 ps-3 alert alert-warning fw-bold text-center">
                      Nenhum participante nesta página.
                    </div>
                  </div>
                </template>

                <!-- Pagination controls -->
                <div
                  v-if="pagination.total > 0"
                  class="d-flex justify-content-between align-items-center flex-wrap mt-3 pb-3"
                  style="margin: 0px 20px;"
                >
                  <span class="text-sm text-secondary">
                    {{ pagination.total }} participante{{ pagination.total === 1 ? "" : "s" }}
                    &middot; {{ pagination.page_size }} por página
                  </span>

                  <div class="d-flex align-items-center">
                    <button
                      class="btn btn-sm bg-gradient-info mb-0 me-2"
                      :disabled="pagination.page <= 1"
                      @click="goToPage(pagination.page - 1)"
                    >
                      <i class="material-icons-round notranslate" style="font-size: 16px;">chevron_left</i>
                    </button>

                    <span class="text-sm font-weight-bold text-dark">
                      Página {{ pagination.page }} de {{ pagination.total_pages }}
                    </span>

                    <button
                      class="btn btn-sm bg-gradient-info mb-0 ms-2"
                      :disabled="pagination.page >= pagination.total_pages"
                      @click="goToPage(pagination.page + 1)"
                    >
                      <i class="material-icons-round notranslate" style="font-size: 16px;">chevron_right</i>
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/views/components/Loading.vue";
import ParticipantsContentTreeTable from "@/views/components/ParticipantsContentTreeTable.vue";
import ParticipantsContentTreeFilters from "@/views/components/ParticipantsContentTreeFilters.vue";
import { http } from "../http/index";

export default {
  name: "ParticipantsByContentTree",

  components: {
    Loading,
    ParticipantsContentTreeTable,
    ParticipantsContentTreeFilters,
  },

  data() {
    return {
      loading: false,
      loading_message: "Carregando participantes",
      error: false,
      errorMessage: "",

      token_user: localStorage.getItem("token"),

      // Project -> course selection: the course_id feeds the report.
      projects: [],
      selectedProjectId: "",
      courses: [],
      selectedCourseId: "",
      coursesLoading: false,

      page: 1,
      page_size: 10,
      filters: {},

      columns: { registration: [], commands: [] },
      rows: [],
      filtersAvailable: [],
      pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
    };
  },

  computed: {
    hasActiveFilters() {
      return Object.keys(this.filters).length > 0;
    },
  },

  mounted() {
    this.getAllProjects();
  },

  methods: {
    getAllProjects() {
      const payload = { token: this.token_user };

      http
        .post("/api/v1/dashboard/list-projects-by-client/", payload)
        .then((response) => {
          const projects = response.data.projects || [];
          // The endpoint can return duplicates; keep the first of each.
          const unique = [];
          const seen = new Set();
          projects.forEach((item) => {
            const key = `${item.id}-${item.name}`;
            if (!seen.has(key)) {
              seen.add(key);
              unique.push(item);
            }
          });
          this.projects = unique;
        })
        .catch(() => {
          this.projects = [];
        });
    },

    getCoursesByProject() {
      this.coursesLoading = true;

      const payload = {
        token_user: this.token_user,
        project_id: this.selectedProjectId,
      };

      return http
        .post("/api/v1/dashboard/list-courses-by-project/", payload)
        .then((response) => {
          this.courses = response.data.courses || [];
        })
        .catch(() => {
          this.courses = [];
        })
        .finally(() => {
          this.coursesLoading = false;
        });
    },

    handleProjectChange() {
      // New project: drop the course and any loaded report.
      this.selectedCourseId = "";
      this.courses = [];
      this.resetReport();

      if (this.selectedProjectId) {
        this.getCoursesByProject();
      } else {
        this.coursesLoading = false;
      }
    },

    handleCourseChange() {
      // New course: start fresh (no inherited filters/page from the previous one).
      this.resetReport();
      if (this.selectedCourseId) {
        this.fetchReport();
      }
    },

    // Clear everything tied to a specific course scope.
    resetReport() {
      this.filters = {};
      this.page = 1;
      this.rows = [];
      this.columns = { registration: [], commands: [] };
      this.filtersAvailable = [];
      this.pagination = { page: 1, page_size: 10, total: 0, total_pages: 0 };
      this.error = false;
      this.errorMessage = "";
    },

    fetchReport() {
      this.loading = true;
      this.error = false;
      this.errorMessage = "";

      const payload = {
        token_user: this.token_user,
        course_id: this.selectedCourseId,
        page: this.page,
        page_size: this.page_size,
        filters: this.filters,
      };

      http
        .post(
          "/api/v1/dashboard/report-participants-by-content-tree/",
          payload
        )
        .then((response) => {
          this.columns = response.data.columns || {
            registration: [],
            commands: [],
          };
          this.rows = response.data.rows || [];
          this.filtersAvailable = response.data.filters_available || [];
          this.pagination = response.data.pagination || {
            page: this.page,
            page_size: this.page_size,
            total: this.rows.length,
            total_pages: 1,
          };
        })
        .catch((err) => {
          this.error = true;
          const apiError =
            err && err.response && err.response.data && err.response.data.error;
          this.errorMessage =
            apiError || "Não foi possível carregar o relatório. Tente novamente.";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    goToPage(targetPage) {
      if (targetPage < 1 || targetPage > this.pagination.total_pages) return;
      if (targetPage === this.pagination.page) return;
      this.page = targetPage;
      this.fetchReport();
    },

    onApplyFilters(filters) {
      this.filters = filters;
      this.page = 1; // any filter change resets to the first page
      this.fetchReport();
    },
  },
};
</script>

<style scoped>
/* Selects: give them a clearly visible border (the theme default washes out
   to near-white) and breathing room for the dropdown arrow on the right. */
.content-tree-select {
  border: 1px solid #d2d6da;
  padding-right: 2.5rem;
  background-position: right 1rem center;
}

.content-tree-select:focus {
  border-color: #35d1f5;
}

/* Initial hint: soft info panel that stays readable (the plain alert-info
   sat too close to the background). */
.hint-box {
  background-color: #f0f8ff;
  color: #344767;
  border: 1px solid #cfe2ff;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}
</style>
